/**
 * Farm-to-farm benchmark report, over months, quarters, half-years or full years.
 *
 * Every string in these shapes is already translated by the backend, including the group
 * headers, the state notes and the period labels. The frontend renders them as-is and translates
 * only its own chrome (buttons, step titles, the export dialog).
 */

/** One selectable animal group with the farms that hold barns of that group. */
export interface FarmToFarmAnimalGroup {
    animal_group_id: number;
    name: string;
    farms: FarmToFarmFarm[];
}

export interface FarmToFarmFarm {
    id: number;
    name: string;
    /** SO.LocationObject.OrderIndex. Decides the left-to-right order of the farm columns. */
    order_index: number;
    barns: FarmToFarmBarn[];
}

export interface FarmToFarmBarn {
    id: number;
    name: string;
}

/**
 * The calendar granularity one column covers. Calendar-fixed, never rolling: Q1 is always
 * Jan-Mar and H1 always Jan-Jun, so two farms in the same column cover the same days.
 *
 * The values are the wire strings the API parses; they are not display text.
 */
export const FARM_TO_FARM_PERIOD_TYPES = ['Month', 'Quarter', 'HalfYear', 'Year'] as const;

export type FarmToFarmPeriodType = (typeof FARM_TO_FARM_PERIOD_TYPES)[number];

export interface FarmToFarmPeriodOption {
    /** "yyyy-MM", "yyyy-Qn", "yyyy-Hn" or "yyyy". Never shown to a user. */
    key: string;
    /**
     * Rendered by the API in the caller's language. Used verbatim - Intl has no quarter or
     * half-year format, so this cannot be rebuilt on the client.
     */
    label: string;
}

/**
 * Every period the selected farms can be compared over, one list per granularity.
 *
 * All four arrive together because they derive from one fact - the earliest registration across
 * the selected farms - so switching the period-type radio needs no request.
 */
export interface FarmToFarmPeriods {
    month: FarmToFarmPeriodOption[];
    quarter: FarmToFarmPeriodOption[];
    half_year: FarmToFarmPeriodOption[];
    year: FarmToFarmPeriodOption[];
}

/**
 * How trustworthy one farm's figures are for one period.
 *
 * `estimated` still carries values - the period-end status was reconstructed rather than
 * registered. `unavailable` and `noData` carry none.
 */
export type FarmToFarmPeriodState = 'actual' | 'estimated' | 'unavailable' | 'noData';

export interface FarmToFarmColumn {
    farm_id: number;
    name: string;
    /** Period key to state. */
    period_states: Record<string, FarmToFarmPeriodState>;
    /** Period key to a translated explanation of a non-actual state, ready to print. */
    period_state_notes: Record<string, string>;
}

export interface FarmToFarmRow {
    field_key: string;
    label: string;
    unit: string;
    /**
     * Decimal places to render with. The value itself is unrounded, so the exported
     * spreadsheet holds the real number rather than a formatted string.
     */
    decimals: number;
    /**
     * Period key to a map of farm id (as a string) or "aggregate" to value.
     * `null` means the figure could not be produced - never render it as zero.
     */
    values: Record<string, Record<string, number | null>>;
}

export interface FarmToFarmGroup {
    key: string;
    header: string;
    rows: FarmToFarmRow[];
}

export interface FarmToFarmReport {
    periods: string[];
    farms: FarmToFarmColumn[];
    groups: FarmToFarmGroup[];
}

/** The key the aggregate column uses inside a row's per-period value map. */
export const FARM_TO_FARM_AGGREGATE_KEY = 'aggregate';

/** Enforced in the API too - the browser is not a trust boundary. */
export const FARM_TO_FARM_MAX_FARMS = 10;

/**
 * The mutable wizard draft, filled in step by step and mutated in place by each step.
 *
 * `animalGroupId` is nullable only to express the state before the location tree has loaded and a
 * group has been picked; it is narrowed to FarmToFarmSelection before the report step, which has no
 * meaningful way to render a draft.
 */
export interface FarmToFarmViewState {
    animalGroupId: number | null;
    selectedFarmIds: number[];
    periodType: FarmToFarmPeriodType;
    /** Period keys, chronological. */
    selectedPeriods: string[];
    /**
     * Key to the label the API rendered for it.
     *
     * Carried through the wizard rather than refetched: the report response holds only keys, and
     * the report step has no period list of its own to look a label up in.
     */
    periodLabels: Record<string, string>;
}

/** A complete selection: what the report step needs, and exactly what the API is asked for. */
export interface FarmToFarmSelection {
    animalGroupId: number;
    farmIds: number[];
    periodType: FarmToFarmPeriodType;
    /** Period keys. */
    periods: string[];
    periodLabels: Record<string, string>;
}
